import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, Loader2, Save } from "lucide-react";
import { toast } from "sonner";
import { AppShell } from "../components/AppShell";
import { NoPermission } from "../components/NoPermission";
import { ClientPicker } from "../components/loans/ClientPicker";
import { DEFAULT_VALUES, LoanField, OPTIONAL_FIELDS, REQUIRED_FIELDS } from "../components/loans/LoanFields";
import { canWrite, useAuth } from "../context/AuthContext";
import { api, errorMessage } from "../lib/api";

const REQUIRED_MSG = "Campo obligatorio";

function buildPayload(mode, client, newClient, values) {
  const fields = { ...values };
  const rate = fields["Effective Interest Rate"];
  if (rate !== undefined && rate !== "") fields["Effective Interest Rate"] = parseFloat((Number(rate) / 100).toFixed(10));
  return mode === "new" ? { new_client: newClient, fields } : { borrower_id: client?.borrower_id, fields };
}

function validate(mode, client, newClient, values) {
  const errors = {};
  REQUIRED_FIELDS.forEach((f) => { if (!String(values[f.key] ?? "").trim()) errors[f.key] = REQUIRED_MSG; });
  if (!errors["Loan Amount"] && Number(values["Loan Amount"]) <= 0) errors["Loan Amount"] = "El monto debe ser mayor a 0";
  if (mode === "existing" && !client) errors.borrower_id = REQUIRED_MSG;
  if (mode === "new" && !newClient.name.trim()) errors.client_name = REQUIRED_MSG;
  if (mode === "new" && !newClient.persona) errors.client_persona = REQUIRED_MSG;
  return errors;
}

const ModeButton = ({ active, onClick, children, testId }) => (
  <button type="button" onClick={onClick} data-testid={testId} aria-pressed={active}
    className={`tr-btn px-4 py-2 text-sm ${active ? "bg-[#0f5ef7] text-white hover:bg-[#0a47be] hover:text-white" : "border border-[#d7e0ec] bg-white text-[#3b475c] hover:bg-[#eef4ff] hover:text-[#0a47be]"}`}>
    {children}
  </button>
);

const NewClientFields = ({ value, onChange, errors }) => (
  <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
    <label className="block">
      <span className="tr-label">Nombre o razón social <span className="text-[#b42318]">*</span></span>
      <input value={value.name} onChange={(e) => onChange({ ...value, name: e.target.value })} className="tr-input" data-testid="new-client-name" />
      {errors.client_name && <p className="tr-field-error" data-testid="error-client-name">{errors.client_name}</p>}
    </label>
    <label className="block">
      <span className="tr-label">Tipo <span className="text-[#b42318]">*</span></span>
      <select value={value.persona} onChange={(e) => onChange({ ...value, persona: e.target.value })} className="tr-input" data-testid="new-client-tipo">
        <option value="">Selecciona…</option>
        <option value="PF">Persona física</option>
        <option value="PM">Persona moral</option>
      </select>
      {errors.client_persona && <p className="tr-field-error" data-testid="error-client-persona">{errors.client_persona}</p>}
    </label>
  </div>
);

function AddLoanForm() {
  const navigate = useNavigate();
  const [mode, setMode] = useState("existing");
  const [client, setClient] = useState(null);
  const [newClient, setNewClient] = useState({ name: "", persona: "" });
  const [values, setValues] = useState(DEFAULT_VALUES);
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);
  const setValue = (k, v) => setValues((s) => ({ ...s, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    const errs = validate(mode, client, newClient, values);
    setErrors(errs);
    if (Object.keys(errs).length) return toast.error("Revisa los campos marcados");
    setSaving(true);
    try {
      const { data } = await api.post("/loans", buildPayload(mode, client, newClient, values));
      toast.success("Crédito guardado");
      navigate(`/clientes/${encodeURIComponent(data.borrower_id)}`);
    } catch (err) {
      setErrors(err.response?.data?.detail?.errors || {});
      toast.error(errorMessage(err, "No se pudo guardar el crédito, intenta de nuevo"));
    } finally {
      setSaving(false);
    }
  };

  return (
    <form onSubmit={submit} noValidate className="space-y-6" data-testid="add-loan-form">
      <section className="tr-card space-y-4 p-5 sm:p-6">
        <div className="tr-eyebrow">1 · Cliente</div>
        <div className="flex flex-wrap gap-2">
          <ModeButton active={mode === "existing"} onClick={() => setMode("existing")} testId="client-mode-existing">Cliente existente</ModeButton>
          <ModeButton active={mode === "new"} onClick={() => setMode("new")} testId="client-mode-new">Cliente nuevo</ModeButton>
        </div>
        {mode === "existing" ? <ClientPicker value={client} onChange={setClient} error={errors.borrower_id} /> : <NewClientFields value={newClient} onChange={setNewClient} errors={errors} />}
        {mode === "new" && <p className="text-xs text-[#5b667a]">Se asignará automáticamente el siguiente ID libre (B-PF-#### o B-PM-####).</p>}
      </section>
      <section className="tr-card space-y-4 p-5 sm:p-6">
        <div className="tr-eyebrow">2 · Datos obligatorios del crédito</div>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
          {REQUIRED_FIELDS.map((f) => <LoanField key={f.key} field={f} value={values[f.key]} error={errors[f.key]} onChange={setValue} required />)}
        </div>
      </section>
      <section className="tr-card space-y-4 p-5 sm:p-6">
        <div className="tr-eyebrow">3 · Datos opcionales</div>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {OPTIONAL_FIELDS.map((f) => <LoanField key={f.key} field={f} value={values[f.key]} error={errors[f.key]} onChange={setValue} />)}
        </div>
      </section>
      <div className="flex flex-wrap justify-end gap-2">
        <button type="button" onClick={() => navigate(-1)} className="tr-btn-light px-4 py-2.5 text-sm" data-testid="cancel-loan-button">Cancelar</button>
        <button type="submit" disabled={saving} className="tr-btn-primary px-5 py-2.5 text-sm" data-testid="save-loan-button">
          {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} Guardar crédito
        </button>
      </div>
    </form>
  );
}

export default function AddLoanPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  if (!canWrite(user)) return <AppShell><NoPermission /></AppShell>;
  return (
    <AppShell>
      <section className="tr-fade-up space-y-3">
        <button type="button" onClick={() => navigate("/")} className="inline-flex items-center gap-1.5 text-sm font-semibold text-[#0f5ef7] hover:text-[#0a47be]" data-testid="add-loan-back">
          <ArrowLeft className="h-4 w-4" /> Volver al panel
        </button>
        <div>
          <div className="tr-eyebrow">Alta manual</div>
          <h1 className="text-2xl font-extrabold tracking-tight text-[#182235] sm:text-3xl">Agregar crédito</h1>
        </div>
      </section>
      <AddLoanForm />
    </AppShell>
  );
}
